import * as React from "react";
import { Button, cn } from "@moanhot/ui";
import { useAdminApi, type AdminInputCheck } from "@/api/adminClient";
import { NUM, TD, TH, when } from "@/screens/format";
import { useAdminAction, useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Problem, Table } from "@/screens/ui";

/** Inputs the pre-run check refused. Internal: partners only see a generic rejection. */
export function Screening() {
  const api = useAdminApi();
  const [load] = useAdminLoad((a) => a.listInputChecks(null), [], "Could not load the log.");
  const [more, setMore] = React.useState<{ rows: AdminInputCheck[]; cursor: string | null } | null>(null);
  const next = useAdminAction();

  return (
    <Page title="Screening log">
      <p className="-mt-3 mb-6 max-w-[72ch] text-sm text-ink-2">Nothing here is automatic. Review a tenant with repeated refusals and suspend it from Tenants if needed.</p>
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="Nothing refused" body="No input has been refused yet." /> : null)}>
        {(d) => {
          const rows = [...d.data, ...(more?.rows ?? [])];
          const cursor = more ? more.cursor : d.next_cursor;
          return (
            <>
              <Table
                label="Refused inputs"
                head={
                  <>
                    <th className={TH}>When</th>
                    <th className={TH}>Tenant</th>
                    <th className={TH}>Reason</th>
                    <th className={TH}>Check</th>
                    <th className={cn(TH, "text-right")}>Score</th>
                    <th className={TH}>Generation or file</th>
                  </>
                }
              >
                {rows.map((r) => (
                  <tr key={r.id}>
                    <td className={cn(TD, "text-xs")}>{when(r.created_at)}</td>
                    <td className={TD}>
                      {r.tenant_name}
                      <div className="font-mono text-xs text-ink-3">
                        {r.tenant_id} {r.mode}
                      </div>
                    </td>
                    <td className={TD}>{r.reason}</td>
                    <td className={cn(TD, "font-mono text-xs")}>{r.detector}</td>
                    <td className={NUM}>{r.confidence === null ? "" : r.confidence.toFixed(2)}</td>
                    <td className={cn(TD, "font-mono text-xs")}>
                      {r.generation_id ?? r.file_id ?? ""}
                      <div className="text-ink-3">{r.request_id}</div>
                    </td>
                  </tr>
                ))}
              </Table>
              {cursor && (
                <Button
                  variant="secondary"
                  className="mt-4"
                  disabled={next.busy}
                  onClick={() => next.run(api.listInputChecks(cursor), (page) => setMore({ rows: [...(more?.rows ?? []), ...page.data], cursor: page.next_cursor }))}
                >
                  Load older
                </Button>
              )}
              <Problem text={next.problem} />
            </>
          );
        }}
      </LoadState>
    </Page>
  );
}
